<template>
  <div class="text-dark">
    <SearchArea :searched="keyword" :category="category" />
    <div class="px-5 md:px-0 md:w-95 max-w-max mx-auto">
      <div class="lg:flex pb-6">
        <div v-if="products.length">
          <div class="pb-6">
            <p v-if="products.length">
              <span class="text-xl font-medium capitalize">
                {{ categoryName || 'Products' }}
              </span>
              <span class="text-xl text-dark pl-3 opacity-50">
                ({{ totalResults }} Products)
              </span>
            </p>
          </div>
          <OnClickOutside :do="() => {showFilters = false}">
            <div class="relative">
              <!-- Filters Opener for small screens -->
              <button
                class="lg:hidden p-3 mb-4 rounded-full border w-full border-dark-b"
                @click="showFilters = !showFilters"
              >
                {{showFilters ? 'Close' : 'Show'}} Filters
              </button>
              <!-- Filters -->
              <div
                class="w-full lg:w-auto text-sm lg:pr-10"
                :class="showFilters ? 'absolute bg-white z-20' : 'hidden lg:block'"
              >
                <div class="lg:w-80 border rounded p-5 shadow-xl lg:shadow-none">
                  <!-- categoriesFilter -->
                  <CategoriesFilter
                    v-if="filters.categories && filters.categories.length"
                    :categorySelected="categoryName"
                    :categories="filters.categories"
                  />
                  <div class="pt-8">
                    <h3 class="text-lg">Price</h3>
                    <div class="pt-3 flex items-center">
                      <div class="rounded-full bg-smoke-100 flex items-center py-2 border border-dark-b">
                        <input
                          type="number"
                          class="app-input bg-smoke-100 w-4/5 text-center"
                          placeholder="from"
                          v-model="range[0]"
                          @input="searchDebounce"
                        >
                        <span>$</span>
                      </div>
                      <span class="mx-1 w-5 border-t border-smoke-b"></span>
                      <div class="rounded-full bg-smoke-100 flex items-center py-2 border border-dark-b">
                        <input
                          type="number"
                          class="app-input bg-smoke-100 w-4/5 text-center"
                          placeholder="to"
                          v-model="range[1]"
                          @input="searchDebounce"
                        >
                        <span>$</span>
                      </div>
                    </div>
                  </div>
                  <div class="pt-8">
                    <h3 class="text-lg">MOQ</h3>
                    <div class="pt-3">
                      <div class="rounded-full bg-smoke-100 flex items-center py-2 border border-dark-b">
                        <input
                          type="number"
                          class="app-input bg-smoke-100 flex-grow text-center"
                          placeholder="Less than"
                          v-model="moq"
                          @input="searchDebounce"
                        >
                      </div>
                    </div>
                  </div>
                  <!-- product certifications -->
                  <Certifications
                   v-if="filters.productCertification && filters.productCertification.length"
                   :certifications= "filters.productCertification"
                   type="Product"
                   @updateCertifications="updateCertificates" 
                  />
                  <!-- supplierCertification -->
                  <Certifications
                   v-if="filters.supplierCertification && filters.supplierCertification.length"
                   :certifications= "filters.supplierCertification" 
                   type="Supplier"
                   @updateCertifications="updateCertificates"
                   @dunsChanged="dunsChanged"
                   @drsChanged="drsChanged"
                  />
                  <!-- supplierLocations -->
                  <SupplierLocations
                    v-if="filters.supplierLocations && filters.supplierLocations.length"
                    :locations="filters.supplierLocations"
                    @updateLocations="updateLocations"
                  />
                  <div class="pt-8" v-if="filters.stockInUsaFilter">
                    <h3 class="text-lg"> Stock Availability</h3>
                    <div
                      class="pt-3 flex items-center checkbox-wrapper"
                      @click="selectStockInUS"
                    >
                      <input
                        type="checkbox"
                        v-model="stockInUS"
                        class="w-5 h-5"
                      >
                      <span class="checkmark" />
                      <img src="@/assets/icons/usa.svg" class="w-6 ml-4">
                      <span class="pl-2 pt-1 pr-2">In USA</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </OnClickOutside>
        </div>
        <!-- Products Section -->
        <div class="primary-section" :class="products.length ? 'w-primary-section' : 'w-full'">
          <div
            class="lg:hidden inset-0 z-10 bg-black bg-opacity-10"
            :class="showFilters ? 'absolute' : 'hidden'"
          />
          <div class="lg:flex">
            <div class="pt-2 w-95 xl:w-85 mx-auto text-base">
              <div v-if="!keyword">
                <span>
                  <router-link to="/" class="text-dark opacity-50 router-">Buy</router-link>
                </span>
                <span class="text-dark" v-for="(link, index) in filters.breadcrumbs" :key="`breadcrumb-link-${index}`">
                  <span class="px-1 opacity-50">/</span>
                  <span v-if="index === filters.breadcrumbs.length-1" class="text-green font-medium">{{ link.categoryName }}</span>
                  <span v-else>
                    <router-link :to="`/${link.categorySlug}`" class="opacity-50">{{ link.categoryName }}</router-link>
                  </span>
                </span> 
              </div>
              <div v-else>
                <span class="text-lg text-dark opacity-50">Displaying {{totalResults > 0 ? (currentPage * 24) - 23 : 0 }} - {{(totalResults > (currentPage * 24)) ? currentPage * 24 : totalResults}} of {{totalResults}} results for </span><span class="text-lg text-blue">"{{keyword}}"</span>
              </div>
            </div>
            <div class="flex items-center justify-end lg:-mt-2 mb-4">
              <div
                class="search-buy-banner"
                :style="{backgroundImage: `url(${backgroundImage})`}"
              >
                <a :href="buyhiveHowToBuyLink" target="_blank">
                  <div class="w-full text-center text-white text-lg"> 
                    Placing bulk orders on
                    <br class="md:hidden">
                    BuyHive is safe & easy.
                    <br>
                    <strong>Click to learn how it works!</strong>
                  </div>
                </a>
              </div>
            </div>
          </div>
          <!-- Products Section Header -->
          <div v-if="products.length" class="flex items-center justify-between">
            <div class="hidden lg:block text-dark text-xl">
              <button @click="view = 'boxView'">
                <i class="icon-boxView" :class="{'opacity-50': view=='listView'}"/>
              </button>
              <button @click="view = 'listView'" class="ml-5">
                <i class="icon-listView"  :class="{'opacity-50': view=='boxView'}"/>
              </button>
            </div>
            <div class="flex items-center">
              <div class="pr-6 lg:hidden">
                Sort by:
              </div>
              <div
                class="flex items-center justify-between border border-dark-b rounded-full bg-smoke-100 py-2 relative mr-6 w-54"
                @mouseenter="showSortingOptions = true"
                @mouseleave="showSortingOptions = false"
              >
                <span class="capitalize pl-4">{{ sortToShow }}</span>
                <img src="@/assets/icons/chevronDown.svg" class="px-4">
                <transition name="slide-down">
                  <div
                    v-if="showSortingOptions"
                    class="sort-options top-full"
                  >
                    <ul class="bg-white p-2 border rounded-lg border border-dark-b">
                      <li
                        v-for="(option, index) in sortingOptions"
                        :key="`sorting-option-${index}`"
                        class="py-1 px-2 cursor-pointer hover:text-green"
                        @click="sortBy(`${option.value}`)"
                      >
                        {{ option.name }}
                      </li>
                    </ul>
                  </div>
                </transition>
              </div>
              <Pagination
                class="hidden lg:flex"
                :total-pages="totalPages"
                :current-page="currentPage"
                @changePage="changePageAndSearch"
              />
            </div>
          </div>
          <!-- Products Listing -->
          <div v-if="products.length" class="pt-12">
            <div
              v-if="view === 'boxView'"
              class="grid grid-cols-2 lg:grid-cols-3 gap-4 xl:gap-10"
            >
              <ProductPreview
                v-for="(product, index) in products"
                :key="`search-product-${index}`"
                :product="product"
              />
            </div>
            <div
              v-else-if="view === 'listView'"
              class=""
            >
              <ProductPreviewInList
                v-for="(product, index) in products"
                :key="`search-product-${index}`"
                :product="product"
              />
            </div>
          </div>
          <div v-else-if="loading" class="pt-10">
            <p class="text-center">Loading...</p>
          </div>
          <div v-else>
            <div class="text-center py-8 border">
              <p class="text-xl text-red-700">Sorry, no products found.</p>
              <p>You may be interested in the Trending Products below.</p>
            </div>
            <TrendingProductsSection class="pt-8" />
            <div class="py-8">
              <div class="text-xl bg-smoke-100 rounded-md py-10 lg:flex items-center justify-center">
                <div class="text-center">Can't find what you are looking for? Tell us which product(s) we can help you source.</div>
                <div class="flex justify-center pt-2 lg:pt-0">
                  <router-link to='/contact-us' class="bg-blue rounded-full py-2 px-4 md:ml-4 text-white" >Contact Us</router-link>
                </div>
              </div>
            </div>
          </div>
          <div v-if="products.length && totalPages" class="flex justify-center lg:justify-end py-12">
            <Pagination
              :total-pages="totalPages"
              :current-page="currentPage"
              @changePage="changePageAndSearch"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { mapActions } from 'vuex'
import SearchArea from '@/shared/components/layout/SearchArea'
import Pagination from '@/shared/components/shared/Pagination'
import debounce from '@/services/debounce'
import ProductPreview from '@/shared/components/product/ProductPreview'
import ProductPreviewInList from '@/shared/components/product/ProductPreviewInList'
import Certifications from './Certifications'
import CategoriesFilter from './CategoriesFilter'
import SupplierLocations from '@/shared/components/product/SupplierLocations'
import TrendingProductsSection from '@/shared/components/home/TrendingProductsSection'
import EventBus from '@/services/eventBus'
import backgroundImage from '@/assets/images/widgetBackground.png'

export default {
  name: 'ProductsSearch',
  components: {
    SearchArea,
    Certifications,
    SupplierLocations,
    ProductPreview,
    ProductPreviewInList,
    Pagination,
    CategoriesFilter,
    TrendingProductsSection
  },
  props: {
    categorySearch: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      backgroundImage,
      buyhiveHowToBuyLink: process.env.VUE_APP_BUYHIVE_HOW_TO_BUY,
      products: [],
      updateRange: true,
      min: -1,
      max: 0,
      showOptions: -1,
      selectedProductCertificates: [],
      selectedSupplierCertificates: [],
      selectedCountries: [],
      range: [],
      filters: {},
      showSortingOptions: false,
      selectedSort: '',
      moq: '',
      showFilters: false,
      sortingOptions: [
        {name: 'Relevance', value: 'relevance'},
        {name: 'Latest', value: 'latest'},
        {name: 'Price Low to High', value: 'price_low_to_high'},
        {name: 'Price High to Low', value: 'price_high_to_low'},
        {name: 'MOQ Low to High', value: 'MOQ_low_to_high'},
        {name: 'Ratings High to Low', value: 'ratings_high_to_low'}
      ],
      duns: false,
      drs: false,
      currentPage: 1,
      totalResults: null,
      stockInUS: false,
      view: 'boxView',
      loading: false
    }
  },
  created() {
    this.loading = true
    this.fetchSearchResult()
    this.setTabTitle()
    EventBus.$on('RESET_FILTERS', () => {
      this.resetFilters()
      this.fetchSearchResult()
    })
  },
  watch: {
    '$route'() {
      this.currentPage = 1
      this.duns = false
      this.drs = false
      this.resetFilters()
      this.fetchSearchResult()
      this.setTabTitle()
      this.updateRange = true
    }
  },
  computed: {
    totalPages() {
      return this.totalResults / 24 > 1 ? Math.ceil(this.totalResults / 24) : 1
    },
    sortToShow() {
      return this.selectedSort.length ? this.selectedSort.replace(/_/g, ' ') : 'relevance'
    },
    keyword() {
      return this.$route.query.keyword
    },
    category() {
      return this.categorySearch
    },
    categoryName() {
      if (!this.category) return null
      if (this.category === 'health_') return 'Health & Wellness'
      else if (this.category === 'ppe') return 'PPE'
      else return this.category.replaceAll('_', ' ')
    }
  },
  methods: {
    ...mapActions ([
      'fetchSearchResults',
      'fetchFiltersForSearchResults'
    ]),
    async fetchSearchResult() {
      let keyword = this.keyword
      let category = this.categorySearch || this.category
      if (category === 'health_') category = 'health_&_wellness'

      keyword = keyword ? keyword : ''
      const payload = {
        keyword: keyword ? keyword : '',
        category: category ? category : '',
        page: this.currentPage
      }
      if (this.range[0] !== this.min || this.range[1] !== this.max) {
        payload.min_price = this.range[0]
        payload.max_price = this.range[1]
      }
      if (this.selectedProductCertificates.length) {
        payload.certificates = this.selectedProductCertificates.join()
      }
      if (this.selectedSupplierCertificates.length) {
        payload.supplier_certificates = this.selectedSupplierCertificates.join()
      }
      if (this.selectedCountries.length) {
        payload.country = this.selectedCountries.join()
      }
      payload.sort_by = this.selectedSort || "default"
      if (this.moq) payload.moq = this.moq
      if (this.duns) payload.has_duns = true
      if (this.drs) payload.has_drs = true
      if (this.stockInUS) payload.stock_in_usa = true
      const response = await this.fetchSearchResults(payload)
      this.products = response.products
      this.loading = false
      this.currentPage = response.currentPage
      this.totalResults = response.totalResults
      let data  = await this.fetchFiltersForSearchResults(payload)
      this.filters = data.filters
      this.setPriceRange()
    },
    setPriceRange() {
      if(this.updateRange) {
        this.min = this.filters.minPrice
        this.max = this.filters.maxPrice
        this.range = [this.min, this.max]
        this.updateRange = false
      }
    },
    updateCertificates(type, data) {
      if(type == "Product") {
        this.selectedProductCertificates = data
      }
      else {
        this.selectedSupplierCertificates = data
      }
      this.changePageAndSearch()
    },
    searchDebounce: debounce(function () {
      this.changePageAndSearch()
    }, 800),
    updateLocations(val) {
      this.selectedCountries = val
      this.changePageAndSearch()
    },
    sortBy(val) {
      this.selectedSort = val
      this.showSortingOptions = false
      this.fetchSearchResult()
    },
    resetFilters() {
      this.selectedProductCertificates = []
      this.selectedSupplierCertificates = []
      this.selectedCountries = []
      this.filters = {}
      this.range = [this.min, this.max]
      this.duns = false
      this.drs = false
      this.stockInUS = false
      this.moq = ''
    },
    dunsChanged(val) {
      this.duns = val
      this.changePageAndSearch()
    },
    drsChanged(val) {
      this.drs = val
      this.changePageAndSearch()
    },
    selectStockInUS() {
      this.stockInUS = !this.stockInUS
      this.changePageAndSearch()
    },
    changePageAndSearch(val = 1) {
      this.currentPage = val
      this.fetchSearchResult()
    },
    setTabTitle() {
      if (this.categorySearch) return
      document.title = this.$route.query.keyword || 'Search'
    }
  }
}
</script>
<style lang="scss" scoped>
  /deep/.vue-slider-dot-handle-focus {
    box-shadow: none;
  }
  .max-w-filters {
    width: 320px;
  }
  /* width */
  /deep/::-webkit-scrollbar {
    width: 4px;
  }

  /* Track */
  /deep/::-webkit-scrollbar-track {
    background: rgba(35, 31, 32, 0.15);
  }

  /* Handle */
  /deep/::-webkit-scrollbar-thumb {
    background: #29B574;
  }
  .sort-options {
    @apply z-20 absolute pt-4 inset-x-0 overflow-hidden duration-200 transition-transform ease-in-out;
    transform-origin: top;
  }
  /deep/.checkbox-wrapper {
    position: relative;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
    cursor: pointer;
    input {
      opacity: 0;
      cursor: pointer;
    }
    .checkmark {
      position: absolute;
      left: 0;
      height: 18px;
      width: 18px;
      border: 1px solid #414a4c;
      border-radius: 4px;
      background-color: #fff;
    }
    .checkmark:after {
      content: "";
      position: absolute;
      display: none;
    }
  }
  /deep/.checkbox-wrapper input:checked ~ .checkmark:after {
    display: block;
  }
  /deep/.checkbox-wrapper input:checked ~ .checkmark {
    background-color: #005CC8;
    border-color: #005CC8;
  }
  /deep/.checkbox-wrapper .checkmark:after {
    left: 5px;
    top: 0px;
    width: 6px;
    height: 13px;
    border: solid white;
    border-width: 0 3px 3px 0;
    -webkit-transform: rotate(45deg);
    -ms-transform: rotate(45deg);
    transform: rotate(45deg);
  }
  .primary-section {
    @apply mt-4 relative;
    @screen lg {@apply mt-0}
    .search-buy-banner {
      @apply w-full p-2 bg-no-repeat bg-cover rounded-lg;
      @screen lg { width: 467px; }
    }
  }
  .w-primary-section {
    @apply w-full;
    @screen lg { width: calc(100% - 22.5rem)}
  }
</style>
